import { DayObject, Timestamp } from "@/types/date"
import { EntityRef, PlaceRef } from "@/types/entity"
import { ItineraryEventProperties, ItineraryEventType } from "@/types/itinerary"
import {
  ActivityPlaceholder,
  EventPlaceholder,
  Place,
  PlacePlaceholder,
  PlaceSummary,
} from "@/types/place"
import {
  WebSearchResult,
  WebSearchResultEvent,
} from "@/types/web-search-result"

import {
  selectMapHoveredPlaceRef,
  setHoveredPlace,
  setMapHoveredPlaceRef,
} from "@/lib/storage/map-hover-slice"
import { AppDispatch, RootState } from "@/lib/storage/redux-store"
import { selectGeocodedSearchResultPlacesByRef } from "@/lib/storage/search-result-map-slice"
import { selectPersistentMessages } from "@/lib/storage/user-slice"
import { addHours, getTimestamp } from "./date-utils"
import { isPlacePlaceholder } from "./entity-utils"

export type SearchResultMapTarget = Place | PlaceSummary | PlacePlaceholder

function isPlaceMapTarget(
  mapTarget: SearchResultMapTarget
): mapTarget is Place {
  return !isPlacePlaceholder(mapTarget)
}

function normalizeSearchResultName(name: string) {
  return name.trim().toLowerCase()
}

export function findVenuePlaceForSearchResult(
  searchResult: WebSearchResult,
  places?: PlaceSummary[]
): PlaceSummary | undefined {
  if (searchResult.type !== "event" || !searchResult.venue.ref || !places) {
    return undefined
  }
  return places.find((p) => p.entity_ref === searchResult.venue.ref)
}

/** Event row in message.places (often has coords when venue.ref is absent on the search result). */
export function findEventPlaceForSearchResult(
  searchResult: WebSearchResultEvent,
  places?: PlaceSummary[]
): PlaceSummary | undefined {
  if (!places) return undefined

  const normalizedName = normalizeSearchResultName(searchResult.name)
  return places.find(
    (place) =>
      place.type === "event" &&
      hasMapCoordinates(place) &&
      normalizeSearchResultName(place.name) === normalizedName
  )
}

export function getSearchResultLocationName(
  searchResult: WebSearchResult
): string | null {
  if (searchResult.type === "event") {
    const { name, location } = searchResult.venue
    const trimmedLocation = location?.trim()
    if (name && trimmedLocation) return `${name}, ${trimmedLocation}`
    if (name) return name
    if (trimmedLocation) return trimmedLocation
    return null
  }
  if (searchResult.type === "trail" && searchResult.start_location) {
    return searchResult.start_location
  }
  return null
}

export function searchResultMapEntityRef(
  messageId: number | string,
  structuredResult: string
): PlaceRef {
  const normalized = structuredResult.replace(/:/g, "-")
  return `ev-wsr-${messageId}-${normalized}` satisfies PlaceRef
}

/** Map hover payload: real coords from mapTarget, synthetic ref per search result. */
export function searchResultMapHoverPlace(
  mapTarget: SearchResultMapTarget,
  messageId: number | string,
  structuredResult: string
): Place | PlacePlaceholder {
  return {
    ...mapTarget,
    entity_ref: searchResultMapEntityRef(messageId, structuredResult),
  }
}

export function setSearchResultMapHover(
  dispatch: AppDispatch,
  mapTarget: SearchResultMapTarget,
  messageId: number | string,
  structuredResult: string
) {
  if (mapTarget.latitude == null || mapTarget.longitude == null) return
  dispatch(
    setHoveredPlace(
      searchResultMapHoverPlace(mapTarget, messageId, structuredResult)
    )
  )
  dispatch(
    setMapHoveredPlaceRef(searchResultMapEntityRef(messageId, structuredResult))
  )
}

export function clearSearchResultMapHover(dispatch: AppDispatch) {
  dispatch(setHoveredPlace(undefined))
  dispatch(setMapHoveredPlaceRef(undefined))
}

export function clearSearchResultMapHoverIfMatches(
  dispatch: AppDispatch,
  getState: () => RootState,
  messageId: number | string,
  structuredResult: string
) {
  const hoverRef = searchResultMapEntityRef(messageId, structuredResult)
  if (selectMapHoveredPlaceRef(getState()) === hoverRef) {
    clearSearchResultMapHover(dispatch)
  }
}

export function searchResultToMapPlaceholder(
  searchResult: WebSearchResult,
  coords: { lat: number; lng: number },
  entityRef: PlaceRef
): EventPlaceholder | ActivityPlaceholder {
  if (searchResult.type === "trail") {
    return {
      type: "activity",
      entity_ref: entityRef,
      name: searchResult.name,
      latitude: coords.lat,
      longitude: coords.lng,
      categories: ["trail"],
    }
  }
  return {
    type: "event",
    entity_ref: entityRef,
    name: searchResult.name,
    latitude: coords.lat,
    longitude: coords.lng,
    categories: ["event"],
    venue_name: searchResult.venue.name ?? undefined,
  }
}

function hasMapCoordinates(
  place: PlaceSummary | PlacePlaceholder
): place is PlaceSummary & { latitude: number; longitude: number } {
  return place.latitude != null && place.longitude != null
}

/** Events only: trails rely on geocoding via start_location. */
export function resolveSearchResultMapTarget(
  searchResult: WebSearchResult,
  places?: PlaceSummary[]
): SearchResultMapTarget | undefined {
  if (searchResult.type !== "event") return undefined

  const venuePlace = findVenuePlaceForSearchResult(searchResult, places)
  if (venuePlace && hasMapCoordinates(venuePlace)) {
    return venuePlace
  }

  const eventPlace = findEventPlaceForSearchResult(searchResult, places)
  if (eventPlace) {
    return eventPlace
  }

  return undefined
}

export function resolveSearchResultMapTargetForTrip(
  searchResult: WebSearchResult,
  places: PlaceSummary[],
  geocodedPlacesByRef: Record<PlaceRef, PlacePlaceholder>,
  messageId?: number | string,
  webSearchResultId?: string
): SearchResultMapTarget | undefined {
  return (
    resolveSearchResultMapTarget(searchResult, places) ??
    (messageId != null && webSearchResultId
      ? geocodedPlacesByRef[
          searchResultMapEntityRef(messageId, webSearchResultId)
        ]
      : undefined)
  )
}

export function isSyntheticSearchResultMapRef(
  entityRef: string | undefined
): boolean {
  return entityRef != null && entityRef.startsWith("ev-wsr-")
}

export function getPlacesForMessageId(
  state: RootState,
  messageId?: number
): PlaceSummary[] {
  if (messageId == null) return []
  const message = selectPersistentMessages(state).find(
    (m) => m.id === messageId
  )
  return message?.places ?? []
}

export function locationPropertiesFromMapTarget(
  mapTarget: SearchResultMapTarget,
  searchResult?: WebSearchResult
): ItineraryEventProperties {
  const properties: ItineraryEventProperties = {}

  if ("formatted_address" in mapTarget && mapTarget.formatted_address) {
    properties.address = mapTarget.formatted_address
  } else if (searchResult) {
    const address = getSearchResultLocationName(searchResult)
    if (address) properties.address = address
  }

  if (mapTarget.latitude != null && mapTarget.longitude != null) {
    properties.latitude = mapTarget.latitude
    properties.longitude = mapTarget.longitude
  }

  if ("city" in mapTarget && mapTarget.city) properties.city = mapTarget.city
  if ("state" in mapTarget && mapTarget.state)
    properties.state = mapTarget.state
  if ("country" in mapTarget && mapTarget.country) {
    properties.country = mapTarget.country
  }
  if ("postal_code" in mapTarget) {
    const postalCode = mapTarget.postal_code
    if (typeof postalCode === "string") {
      properties.postal_code = postalCode
    }
  }

  return properties
}

export interface WebSearchResultTripLocation {
  place?: Place
  entityRef?: EntityRef
  properties?: ItineraryEventProperties
}

export function resolveWebSearchResultTripLocation(
  state: RootState,
  {
    searchResult,
    messageId,
    webSearchResultId,
  }: {
    searchResult?: WebSearchResult
    messageId?: number
    webSearchResultId?: string
  }
): WebSearchResultTripLocation {
  if (!searchResult) return {}

  const places = getPlacesForMessageId(state, messageId)
  const geocodedPlacesByRef = selectGeocodedSearchResultPlacesByRef(state)
  const mapTarget = resolveSearchResultMapTargetForTrip(
    searchResult,
    places,
    geocodedPlacesByRef,
    messageId,
    webSearchResultId
  )

  const properties: ItineraryEventProperties = {
    web_search_result: searchResult,
  }

  let place: Place | undefined
  let entityRef: EntityRef | undefined

  if (mapTarget) {
    Object.assign(
      properties,
      locationPropertiesFromMapTarget(mapTarget, searchResult)
    )
    if (!isSyntheticSearchResultMapRef(mapTarget.entity_ref)) {
      entityRef = mapTarget.entity_ref
      if (isPlaceMapTarget(mapTarget)) {
        place = mapTarget
      }
    }
  } else {
    const address = getSearchResultLocationName(searchResult)
    if (address) properties.address = address
  }

  return { place, entityRef, properties }
}

export function spreadWebSearchResultTripLocation(
  state: RootState,
  params: {
    searchResult?: WebSearchResult
    messageId?: number
    webSearchResultId?: string
  }
): Pick<WebSearchResultTripLocation, "place" | "properties"> {
  const { place, properties } = resolveWebSearchResultTripLocation(
    state,
    params
  )
  return {
    ...(place ? { place } : {}),
    ...(properties ? { properties } : {}),
  }
}

export interface PrepareEventFromWebSearchResultParams {
  title: string
  searchResult?: WebSearchResult
  tripStartDate: DayObject
  tripTimeZone: string
  messageId?: number
  webSearchResultId?: string
}

export interface PreparedEventParams {
  type: ItineraryEventType
  title: string
  start: Timestamp
  end: Timestamp
  timeZone: string
  defaultTime: boolean
  messageId?: number
  webSearchResultId?: string
}

export interface PreparedWebSearchResultForTrip extends PreparedEventParams {
  entityRef?: EntityRef
  properties?: ItineraryEventProperties
  place?: Place
}

/**
 * prepares event creation parameters from a web search result.
 * if the search result is an event with actual schedule times, uses those.
 * otherwise, defaults to 10am on the trip start date.
 */
export function prepareEventFromWebSearchResult({
  title,
  searchResult,
  tripStartDate,
  tripTimeZone,
  messageId,
  webSearchResultId,
}: PrepareEventFromWebSearchResultParams): PreparedEventParams {
  const isEvent = searchResult?.type === "event"
  let startTimestamp: Timestamp
  let endTimestamp: Timestamp
  let eventTimeZone: string | undefined
  let hasActualEventTimes = false

  if (
    isEvent &&
    searchResult &&
    searchResult.type === "event" &&
    searchResult.schedule?.start_at
  ) {
    startTimestamp = searchResult.schedule.start_at
    endTimestamp = searchResult.schedule.end_at
      ? searchResult.schedule.end_at
      : addHours(startTimestamp, 1)
    eventTimeZone = searchResult.schedule.time_zone
    hasActualEventTimes = true
  } else {
    startTimestamp = getTimestamp(
      tripStartDate,
      { hour: 10, minute: 0 },
      tripTimeZone
    )
    endTimestamp = addHours(startTimestamp, 1)
  }

  return {
    type: isEvent ? "event" : "generic",
    title,
    start: startTimestamp,
    end: endTimestamp,
    timeZone: eventTimeZone || tripTimeZone,
    defaultTime: !hasActualEventTimes,
    messageId,
    webSearchResultId,
  }
}

export function prepareWebSearchResultForTrip(
  state: RootState,
  params: PrepareEventFromWebSearchResultParams
): PreparedWebSearchResultForTrip {
  const eventParams = prepareEventFromWebSearchResult(params)
  const { place, entityRef, properties } = resolveWebSearchResultTripLocation(
    state,
    {
      searchResult: params.searchResult,
      messageId: params.messageId,
      webSearchResultId: params.webSearchResultId,
    }
  )

  return {
    ...eventParams,
    place,
    entityRef,
    properties,
  }
}
